import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { combineLatest, distinctUntilChanged, map, Observable, startWith, Subscription } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { Store, StoreService, Vendor, VendorService } from '../..';
import { MatSidenav } from '@angular/material/sidenav';
import { Filter } from 'src/app/@shared/models/filter.model';
import { MatOptionSelectionChange } from '@angular/material/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

const log = new Logger('StoresComponent');

@Component({
  selector: 'app-vendors',
  templateUrl: './vendors.component.html',
  styleUrls: ['./vendors.component.scss']
})

export class VendorsComponent<T extends Vendor> implements OnInit {

  @ViewChild(MatSidenav) sidenav!: MatSidenav;

  viewModel$ = combineLatest([
    this.vendorService.vendors$,
    this.vendorService.isLoading$,
    this.vendorService.totalRecords$,
    this.vendorService.page$,
  ]).pipe(
    map(([vendors, isLoading, totalRecords, page]) => {
      if (vendors.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.vendorService.page(page);
        this.vendorService.reload();
      }
      return { vendors, isLoading, totalRecords, page }
    }),
  );

  filtersForm = new FormGroup({
    search: new FormControl<string | null>(null)
  });

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'VendorName', 'Id', 'ClientKey', 'Actions'];
  vendorList: Vendor[] = [];
  selection = new SelectionModel<Vendor>(true, []);


  constructor(private vendorService: VendorService<T>, private mediaObserver: MediaObserver, private dialog: MatDialog,
    private matSnackBar: MatSnackBar) { }

  ngOnInit(): void {
    log.debug('init');
    this.vendorService.vendors$.subscribe((data) => {
      this.vendorList = data;
    })
    this.clearSearch();
    this.vendorService.reload();

    this.filtersForm.controls.search.setValue(this.vendorService.search);

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'VendorName', 'Actions'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'VendorName', 'ClientKey', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'VendorName', 'Id', 'ClientKey', 'Actions'];
        }
      });
  }

  onSearch(event: any) {
    this.vendorService.search = event.target.value;
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.vendorService.search = '';
  }



  onSort(sortState: Sort): void {
    this.vendorService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.vendorService.page(pageEvent);
  }


  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.vendorList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.vendorList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  downloadJSON() {
    this.vendorService.downloadVendorsJSON();
  }

  downloadCSV() {
    this.vendorService.downloadVendorsCSV();
  }


  deleteSelectedRecords() {
    let selectedRecords: any;
    selectedRecords = this.getSelectedSectionRecords();
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Vendor(s)?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            this.vendorService.deleteVendors(selectedRecords).subscribe({
              next: () => {
                this.matSnackBar.open(`Vendor(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                this.vendorService.reload();
                this.clearSelection();
              },
              error: (error) => {
                log.error('Error in deleting Store', error);

                if (error.error.value) {
                  throw new Error(error.error.value);
                } else {
                  throw new Error(error.message);
                }
              }
            });
          }
        });
    }
  }
}
